import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { attackPathNodes, attackPathOrder, type AttackPathNodeType } from '@/tokens/attackPath';

/**
 * AttackPathLegend
 * Classification: custom
 *
 * Domain-specific legend that documents the attack-path node color system
 * (stroke + fill per node type) directly from design tokens. Used alongside
 * attack-path graphs and investigation views.
 */
export interface AttackPathLegendProps {
  /** Restrict to a subset of node types (defaults to all, in canonical order). */
  types?: AttackPathNodeType[];
  orientation?: 'horizontal' | 'vertical';
}

export function AttackPathLegend({
  types = attackPathOrder,
  orientation = 'horizontal',
}: AttackPathLegendProps) {
  return (
    <Box
      role="list"
      aria-label="Attack path legend"
      sx={{
        display: 'flex',
        flexDirection: orientation === 'horizontal' ? 'row' : 'column',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      {types.map((type) => {
        const node = attackPathNodes[type];
        return (
          <Box key={type} role="listitem" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              aria-hidden
              sx={{
                width: 20,
                height: 20,
                borderRadius: 1,
                backgroundColor: node.fill,
                border: `2px solid ${node.stroke}`,
                flexShrink: 0,
              }}
            />
            <Typography variant="body1">{node.label}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}
